import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  HStack,
  Icon,
  Image,
  Button,
  Badge,
  Avatar,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Link
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import {
  FiLogOut,
  FiShoppingCart,
  FiHome,
  FiLogIn,
  FiTool,
  FiRepeat,
  FiUser,
  FiBook
} from 'react-icons/fi';
import { FaXTwitter, FaInstagram, FaLinkedin, FaYoutube, FaFacebook, FaTiktok } from 'react-icons/fa6';
import { useAuth } from '../contexts/AuthContext';
import { useBasket } from '../contexts/BasketContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/api';

function TopNavbar() {
  const { userMode, switchMode, logout, authState, username, isAuthenticated } = useAuth();
  const { itemCount } = useBasket();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();


  const socialLinks = [
    { icon: FaXTwitter, href: 'https://x.com/naturespulse_hq?s=21' },
    { icon: FaInstagram, href: 'https://www.instagram.com/naturespulseofficial?igsh=c250aGZtNWQ1MXhs&utm_source=qr' },
    { icon: FaLinkedin, href: 'https://www.linkedin.com/company/nature-s-pulse/' },
    { icon: FaYoutube, href: 'https://youtube.com/@naturespulse-00?si=Fb0JKL67jXqrb9g2' },
    { icon: FaFacebook, href: 'https://www.facebook.com/share/16k2veKrcj/?mibextid=wwXIfr' },
    { icon: FaTiktok, href: 'https://www.tiktok.com/@esther.oko4?_t=ZN-8yJhihLcifu&_r=1' },
  ];

  // Move all useColorModeValue hooks to the top level
  const textColor = useColorModeValue('white', 'white');
  const menuTextColor = useColorModeValue('ink.primary', 'white');
  const buyerBgColor = useColorModeValue('forest.800', 'forest.800');
  const sellerBgColor = useColorModeValue('forest.900', 'forest.900');
  const hoverBgColor = useColorModeValue('whiteAlpha.200', 'whiteAlpha.200');
  const menuListBg = useColorModeValue('#FBF9F5', '#1A202C');
  const menuListBorder = useColorModeValue('surface.border', 'gray.600');
  const menuItemHoverBg = useColorModeValue('warm.100', 'gray.700');
  const menuIconBg = useColorModeValue('white', 'gray.600');
  const dividerBg = useColorModeValue('surface.border', 'gray.600');
  const logoutHoverBg = useColorModeValue('red.50', 'red.900');
  const logoutIconBg = useColorModeValue('red.50', 'red.900');
  const socialSectionBg = useColorModeValue('forest.700', 'forest.800');


  // Enhanced color scheme with premium aesthetics
  const isBuyer = userMode === 'buyer';
  const bgColor = isBuyer ? buyerBgColor : sellerBgColor;

  const getPageTitle = () => {
    const titleMap = {
      '/': 'Products',
      '/seller-dashboard': 'Seller Dashboard',
      '/buyer-dashboard': 'Buyer Dashboard',
      '/product-listing': 'Products',
      '/messages': 'Messages',
      '/notifications': 'Notifications',
      '/seller-profile': 'Profile'
    };
    return titleMap[location.pathname] || '';
  };

  const handleModeSwitch = async () => {
    const newMode = userMode === 'buyer' ? 'seller' : 'buyer';

    if (newMode === 'seller') {
      try {
        const { user } = authState;

        if (!user?.id) {
          throw new Error('User authentication required');
        }

        const response = await axiosInstance.get(`/api/sellers/${user.id}`);
        const seller = response.data;

        if (seller.status === 'approved') {
          switchMode(newMode);
          navigate(`/${newMode}-dashboard`, { replace: true });
        } else if (seller.status === 'pending' || seller.status === 'rejected') {
          navigate('/seller-application-status');
        } else {
          navigate('/seller-signup');
        }
      } catch (error) {
        console.error('Error checking seller status:', error);
        navigate('/seller-signup');
      }
    } else {
      switchMode(newMode);
      navigate(`/${newMode}-dashboard`, { replace: true });
    }
  };

  // Custom Store Icon Component (since FiStore doesn't exist)
  const StoreIcon = () => (
    <Box display="inline-flex" alignItems="center" justifyContent="center">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />
        <path d="M3 9h18" />
        <path d="M6 12h4" />
        <path d="M6 15h4" />
        <path d="M14 12h4" />
      </svg>
    </Box>
  );

  const menuItems = isAuthenticated() ? [
    { icon: FiHome, label: 'Home', action: () => navigate('/home'), description: 'Go to homepage' },
    {
      icon: StoreIcon,
      label: 'Start Selling',
      description: 'begin your selling journey',
      action: () => navigate('/sellers')
    },
    ...(isAuthenticated() && authState.user?.roles?.includes?.('seller') ? [{
      icon: FiRepeat,
      label: `Switch to ${userMode === 'buyer' ? 'Seller' : 'Buyer'}`,
      description: `Switch to your ${userMode === 'buyer' ? 'seller' : 'buyer'} account`,
      action: handleModeSwitch
    }] : []),
    ...(isAuthenticated() && userMode === 'seller' ? [{
      icon: FiUser,
      label: 'Profile',
      description: 'View and edit your seller profile',
      action: () => navigate('/seller-profile')
    }] : []),
    {
      icon: FiTool,
      label: 'Pulse Agent',
      description: 'Pulse Agent portal',
      action: () => {
        if (isAuthenticated() && authState.user?.roles?.includes?.('agent')) {
          navigate('/agent-dashboard');
        } else {
          navigate('/pulse-agents');
        }
      }
    },

    ...(authState.user?.has_pulse_hub_access ? [{
      icon: FiBook,
      label: 'Pulse Hub',
      description: 'Access Pulse Hub content',
      action: () => navigate('/hub')
    }] : []),
  ] : [
    { icon: FiLogIn, label: 'Login', action: () => navigate('/login'), description: 'Sign in to your account' },
    { icon: FiHome, label: 'Home', action: () => navigate('/home'), description: 'Go to homepage' },
    { icon: StoreIcon, label: 'Start Selling', action: () => navigate('/sellers'), description: 'Begin your selling journey' },
    { icon: FiTool, label: 'Agent Portal', action: () => navigate('/pulse-agents'), description: 'Join our logistics team' },
  ];
  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bg={bgColor}
      backdropFilter="blur(20px)"
      borderBottom="1px solid"
      borderColor="whiteAlpha.200"
      zIndex={1000}
      transition="all 0.3s ease"
    >
      <Box
        bg="linear-gradient(135deg, transparent 0%, whiteAlpha.100 100%)"
        px={{ base: 4, md: 6 }}
        py={{ base: 3, md: 4 }}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          maxW="1400px"
          mx="auto"
          minH="44px"
        >
          {/* Premium Logo Section */}
          <Flex alignItems="center" minW={{ base: "40px", md: "60px" }}>
            <Box
              as="button"
              onClick={() => navigate('/home')}
              cursor="pointer"
              position="relative"
              aria-label="Nature's Pulse Home"
              _before={{
                content: '""',
                position: 'absolute',
                inset: '-2px',
                borderRadius: 'full',
                background: 'linear-gradient(45deg, whiteAlpha.400, whiteAlpha.100)',
                zIndex: -1,
              }}
            >
              <Image
                src="/naturespulse.jpg"
                alt="Nature's Pulse Logo"
                boxSize={{ base: "36px", md: "42px" }}
                objectFit="contain"
                rounded="full"
                border="2px solid white"
                shadow="md"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
              />
            </Box>
          </Flex>

          {/* Enhanced Title Section */}
          <Flex justify="center" align="center" flex="1" px={4} gap={2}>
            <Text
              fontWeight="700"
              fontSize={{ base: "md", md: "lg", lg: "xl" }}
              color={textColor}
              textAlign="center"
              letterSpacing="-0.02em"
              textShadow="0 1px 2px rgba(0,0,0,0.15)"
              transition="all 0.2s ease"
            >
              {getPageTitle()}
            </Text>
            {!isBuyer && (
              <Badge
                bg="harvest.500"
                color="forest.900"
                fontSize="xs"
                fontWeight="800"
                px={2}
                py={0.5}
                rounded="full"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                Seller Ops
              </Badge>
            )}
          </Flex>

          {/* Premium Controls Section */}
          <HStack spacing={{ base: 2, md: 3 }}>
            {/* Enhanced Cart Button for Buyers with 48px min touch target */}
            {isBuyer && (
              <Box position="relative">
                <IconButton
                  icon={<Icon as={FiShoppingCart} boxSize={5} />}
                  variant="ghost"
                  aria-label="Open basket"
                  onClick={() => navigate('/basket')}
                  color="white"
                  minW="48px"
                  minH="48px"
                  rounded="xl"
                  _hover={{
                    bg: hoverBgColor,
                    transform: 'translateY(-1px)',
                  }}
                  transition="all 0.2s ease"
                />
                {itemCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-1"
                    right="-1"
                    colorScheme="red"
                    rounded="full"
                    fontSize="xs"
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                    shadow="md"
                    border="2px solid white"
                  >
                    {itemCount > 99 ? '99+' : itemCount}
                  </Badge>
                )}
              </Box>
            )}

            {/* Drawer Menu with 48px min touch target */}
            <IconButton
              icon={<HamburgerIcon boxSize={5} />}
              variant="ghost"
              aria-label="Open menu"
              onClick={onOpen}
              color="white"
              minW="48px"
              minH="48px"
              rounded="xl"
              _hover={{
                bg: hoverBgColor,
                transform: 'translateY(-1px)',
              }}
              _active={{ transform: 'scale(0.95)' }}
              transition="all 0.2s ease"
            />
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
              <DrawerOverlay />
              <DrawerContent bg={menuListBg} color={menuTextColor} maxW="70vw" w="70vw">
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" borderColor={menuListBorder}>
                  {isAuthenticated() ? (
                    <Flex alignItems="center">
                      <Box position="relative">
                        <Avatar
                          size="md"
                          src={authState?.user?.profile_picture_url}
                          {...(!authState?.user?.profile_picture_url && { icon: <Icon as={FiUser} /> })}
                          bg={bgColor}
                          color="white"
                          shadow="lg"
                          border="3px solid white"
                          cursor="default"
                          _hover={{ transform: 'none' }}
                          transition="none"
                        />
                      </Box>
                      <Box ml={4} flex="1">
                        <Text fontWeight="700" fontSize="lg" cursor="default">
                          {username || 'Guest'}
                        </Text>
                        <Flex alignItems="center" mt={1}>
                          <Box
                            w="8px"
                            h="8px"
                            bg={bgColor}
                            rounded="full"
                            mr={2}
                          />
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textTransform="capitalize"
                            fontWeight="500"
                          >
                            {userMode || 'Unknown'} Account
                          </Text>
                        </Flex>
                      </Box>
                    </Flex>
                  ) : (
                    <Text>Menu</Text>
                  )}
                </DrawerHeader>
                <DrawerBody p={0}>
                  <Flex direction="column" justify="space-between" h="full">
                    <VStack align="stretch" spacing={2} p={4} flex="1">
                      {menuItems.map((item, index) => (
                        <Button
                          key={index}
                          onClick={() => {
                            item.action();
                            onClose();
                          }}
                          variant="ghost"
                          justifyContent="start"
                          py={6}
                          px={4}
                          rounded="xl"
                          _hover={{
                            bg: menuItemHoverBg,
                            transform: 'translateX(4px)',
                            shadow: 'md'
                          }}
                          transition="all 0.3s ease"
                        >
                          <Flex alignItems="center" w="full">
                            <Box
                              p={2}
                              rounded="lg"
                              bg={menuIconBg}
                              shadow="sm"
                              mr={4}
                              color={bgColor}
                            >
                              {typeof item.icon === 'function' ? (
                                <item.icon />
                              ) : (
                                <Icon as={item.icon} boxSize={5} />
                              )}
                            </Box>
                            <Box flex="1" textAlign="left">
                              <Text fontWeight="600" mb={1}>
                                {item.label}
                              </Text>
                              {item.description && (
                                <Text fontSize="xs" color="gray.500">
                                  {item.description}
                                </Text>
                              )}
                            </Box>
                          </Flex>
                        </Button>
                      ))}
                      {isAuthenticated() && (
                        <>
                          <Box px={4} py={2}>
                            <Box h="1px" bg={dividerBg} />
                          </Box>
                          <Button
                            onClick={() => {
                              logout();
                              onClose();
                            }}
                            variant="ghost"
                            justifyContent="start"
                            py={6}
                            px={4}
                            rounded="xl"
                            _hover={{
                              bg: logoutHoverBg,
                              transform: 'translateX(4px)'
                            }}
                            transition="all 0.3s ease"
                            w="full"
                          >
                            <Flex alignItems="center" w="full">
                              <Box
                                p={2}
                                rounded="lg"
                                bg={logoutIconBg}
                                shadow="sm"
                                mr={4}
                                color="red.500"
                              >
                                <Icon as={FiLogOut} boxSize={5} />
                              </Box>
                              <Box flex="1" textAlign="left">
                                <Text fontWeight="600" mb={1} color="red.500">
                                  Log Out
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Securely log out of your account
                                </Text>
                              </Box>
                            </Flex>
                          </Button>
                        </>
                      )}
                    </VStack>

                    {/* Fixed Social Section */}
                    <Box
                      bg="green.500"
                      borderTopRadius="xl"
                      p={6}
                      mt="auto"
                      position="relative"
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bg: 'green.500',
                        borderTopRadius: 'xl',
                        zIndex: -1
                      }}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        color="white"
                        textAlign="center"
                        mb={4}
                      >
                        Follow Us
                      </Text>
                      <HStack spacing={3} justify="center" wrap="wrap">
                        {socialLinks.map((social, index) => {
                          // Define brand colors for each social platform
                          const getBrandColor = (icon) => {
                            switch (icon) {
                              case FaXTwitter: return '#000000'; // X/Twitter black
                              case FaInstagram: return '#E4405F'; // Instagram gradient pink
                              case FaLinkedin: return '#0077B5'; // LinkedIn blue
                              case FaYoutube: return '#FF0000'; // YouTube red
                              case FaFacebook: return '#1877F2'; // Facebook blue
                              case FaTiktok: return '#FF0050'; // TikTok pink/red
                              default: return 'white';
                            }
                          };

                          const brandColor = getBrandColor(social.icon);

                          return (
                            <Link key={index} href={social.href} isExternal>
                              <IconButton
                                aria-label={`Follow us on ${social.href}`}
                                icon={<Icon as={social.icon} boxSize={5} />}
                                variant="ghost"
                                color="white"
                                size="lg"
                                bg={socialSectionBg}
                                _hover={{
                                  color: 'white',
                                  bg: brandColor,
                                  transform: 'scale(1.1)',
                                  shadow: `0 0 20px ${brandColor}40`
                                }}
                                rounded="full"
                                transition="all 0.3s ease"
                                border="2px solid transparent"
                                _active={{
                                  transform: 'scale(0.95)'
                                }}
                              />
                            </Link>
                          );
                        })}
                      </HStack>
                    </Box>
                  </Flex>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}

export default TopNavbar;
